/**
 * NICEPAY Payment Utils
 * FSD: features/subscription/checkout/lib
 *
 * 나이스페이 결제창을 띄우기 위한 유틸 함수들
 */

import type { NicepayPaymentRequest } from "./nicepay.types";

/**
 * NICEPAY 결제창 요청
 * @param paymentData - 결제 요청 데이터
 */
export const requestNicepayPayment = (
  paymentData: NicepayPaymentRequest
): void => {
  console.log(
    "🔵 requestNicepayPayment called with:",
    JSON.stringify(paymentData, null, 2)
  );

  // 1. 결제 데이터 유효성 검증
  if (!validatePaymentData(paymentData)) {
    console.error("❌ Payment validation failed");
    alert("결제 정보가 올바르지 않습니다.");
    return;
  }

  // 2. NICEPAY 스크립트 로드 확인
  if (typeof (window as any).goPay !== "function") {
    console.error("goPay function not available. Script may not be loaded.");
    alert("결제 모듈을 로드할 수 없습니다. 잠시 후 다시 시도해주세요.");
    return;
  }

  // 3. 결제 폼 생성 및 제출
  const form = createPaymentForm(paymentData);
  document.body.appendChild(form);

  // 4. goPay 함수 호출 - 결제창 띄우기
  console.log("🚀 Calling goPay with form");
  try {
    (window as any).goPay(form);
  } catch (err) {
    console.error("❌ Error calling goPay:", err);
    throw err;
  }

  // // 5. 결제 후 폼 제거 (2초 후)
  // setTimeout(() => {
  //   try {
  //     if (form && form.parentNode && document.body.contains(form)) {
  //       document.body.removeChild(form);
  //     }
  //   } catch (err) {
  //     // 에러가 발생해도 결제는 계속 진행
  //     console.log("!! ERROR: ", err);
  //   }
  // }, 2000);
};

/**
 * 결제 데이터 유효성 검증
 */
const validatePaymentData = (data: NicepayPaymentRequest): boolean => {
  if (!data.goodsName || !data.amt || !data.merchantId || !data.moid) {
    console.error("Missing required fields:", {
      goodsName: data.goodsName,
      amt: data.amt,
      merchantId: data.merchantId,
      moid: data.moid,
    });
    return false;
  }
  if (!data.buyerName || !data.buyerEmail) {
    console.error("Missing buyer info:", {
      buyerName: data.buyerName,
      buyerEmail: data.buyerEmail,
    });
    return false;
  }

  // amt를 숫자로 변환하고 정수로 처리 (문자열일 수 있음)
  const amt =
    typeof data.amt === "string"
      ? parseInt(data.amt, 10)
      : Math.floor(data.amt);

  if (amt < 100) {
    // 최소 결제 금액: 100원
    console.error("Amount too low:", amt);
    return false;
  }

  if (!Number.isInteger(amt)) {
    console.error("Amt must be an integer value:", data.amt);
    return false;
  }

  if (!data.signData || !data.ediDate) {
    console.error("Missing payment signature:", {
      signData: data.signData,
      ediDate: data.ediDate,
    });
    return false;
  }

  return true;
};

/**
 * 결제 폼 생성
 */
const createPaymentForm = (data: NicepayPaymentRequest): HTMLFormElement => {
  const form = document.createElement("form");
  form.name = "payForm";
  form.method = "post";
  // form.action = "https://api-dev.goodgrowthplanner.com/v1/payment/approve";
  form.action = "http://localhost:3001/v1/payment/approve";
  form.acceptCharset = "utf-8";
  form.style.display = "none";

  // 필수 필드
  addFormField(form, "GoodsName", data.goodsName);

  // Amt - 정수 값만 설정 가능 (nicepay-pgweb 요구사항)
  const intAmt =
    typeof data.amt === "string"
      ? parseInt(data.amt, 10)
      : Math.floor(data.amt);
  console.log("🔢 Converting Amt:", { original: data.amt, converted: intAmt });
  addFormField(form, "Amt", String(intAmt));
  addFormField(form, "MID", data.merchantId);
  addFormField(form, "Moid", data.moid);
  addFormField(form, "BuyerName", data.buyerName);
  addFormField(form, "BuyerEmail", data.buyerEmail);

  // 선택 필드
  if (data.buyerTel) {
    addFormField(form, "BuyerTel", data.buyerTel);
  }
  if (data.returnUrl) {
    addFormField(form, "ReturnURL", data.returnUrl);
  }
  if (data.vbankExpDate) {
    addFormField(form, "VbankExpDate", data.vbankExpDate);
  }

  // 고정 필드
  addFormField(form, "NpLang", "KO");
  addFormField(form, "GoodsCl", "1");
  addFormField(form, "TransType", "0");
  addFormField(form, "CharSet", "utf-8");

  // 정기결제 필드
  if (data.isRecurring) {
    addFormField(form, "IsRecurring", "Y");
    addFormField(form, "RecurringInterval", data.recurringInterval || "M");
    addFormField(form, "BillAuthYN", "Y");
    addFormField(form, "CardInterest", "");
    addFormField(form, "CardQuota", "");

    if (typeof data.billingKey === "string" && data.billingKey.trim() !== "") {
      addFormField(form, "BillingKey", data.billingKey);
    }
  }

  // PayMethod (정기결제 설정 이후)
  addFormField(form, "PayMethod", "CARD");

  // 서명 데이터 (백엔드에서 생성)
  addFormField(form, "EdiDate", data.ediDate);
  addFormField(form, "SignData", data.signData || "");

  // AuthToken (있으면 추가)
  if (data.authToken) {
    addFormField(form, "AuthToken", data.authToken);
  }

  // 폼 데이터 로깅 (디버그용)
  console.log("📋 Form fields created:");
  const inputs = form.querySelectorAll("input");
  inputs.forEach((input) => {
    console.log(`  ${input.name}: ${input.value}`);
  });

  return form;
};

const addFormField = (
  form: HTMLFormElement,
  name: string,
  value: string | undefined
): void => {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value || "";
  form.appendChild(input);
};

/**
 * EDI Date 생성 (YYYYMMDDHHMISS)
 */
const generateEdiDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const date = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");

  return `${year}${month}${date}${hours}${minutes}${seconds}`;
};

/**
 * 나이스페이 결제 완료 콜백 처리
 * (백엔드에서 authReq 후 리다이렉트됨)
 */
export const handleNicepayCallback = (
  onSuccess?: ((response: any) => void) | null,
  onError?: ((error: any) => void) | null
): void => {
  // URL 파라미터에서 결제 결과 추출
  const searchParams = new URLSearchParams(window.location.search);
  const resultCode = searchParams.get("resultCode");
  const resultMsg = searchParams.get("resultMsg");

  if (resultCode === "0000") {
    // 성공
    onSuccess?.({
      resultCode,
      resultMsg,
      tid: searchParams.get("tid"),
      moid: searchParams.get("moid"),
    });
  } else {
    // 실패
    onError?.({
      resultCode,
      resultMsg: resultMsg || "결제 처리 중 오류가 발생했습니다.",
    });
  }
};

/**
 * 나이스페이 결제 취소
 * (결제창 닫기 시 호출)
 */
export const handleNicepayClose = (): void => {
  alert("결제가 취소 되었습니다");
};

/**
 * SignData 검증 (위변조 감지)
 * - 백엔드에서 전달한 SignData가 올바른지 검증
 * - NICEPAY 결제 시 필수 검증 단계
 *
 * @param ediDate - 결제 일시 (YYYYMMDDHHMISS)
 * @param merchantId - 상점 ID
 * @param amount - 결제 금액
 * @param receivedSignData - 백엔드에서 받은 SignData
 * @param secretKey - 상점 Secret Key (백엔드에서 관리)
 * @returns SignData 유효성 여부
 */
export const validateSignData = (
  ediDate: string,
  merchantId: string,
  amount: number | string,
  receivedSignData: string,
  secretKey: string
): boolean => {
  console.log("=== SignData Validation ===");
  console.log("EdiDate:", ediDate);
  console.log("MerchantId:", merchantId);
  console.log("Amount:", amount);
  console.log("Received SignData:", receivedSignData);

  // 검증 문자열 생성: ediDate + merchantId + amount + secretKey
  const validationString = `${ediDate}${merchantId}${amount}${secretKey}`;
  console.log("Validation String:", validationString);

  // 실제 환경에서는 SHA256 해시 필요
  // 현재는 백엔드에서 검증하고, 프론트엔드는 받은 SignData가 유효한지만 확인
  // 프론트엔드에서 SHA256 검증하려면 crypto-js 또는 TweetNaCl.js 필요

  // 기본 검증: SignData가 64글자 hex string인지 확인 (SHA256 해시는 64글자)
  const isValidFormat = /^[a-f0-9]{64}$/i.test(receivedSignData);

  if (!isValidFormat) {
    console.error("❌ SignData format is invalid. Expected SHA256 hex hash.");
    return false;
  }

  console.log("✅ SignData format is valid");
  return true;
};

/**
 * 프론트엔드에서 SignData 생성 (테스트용)
 * 실제 운영 환경에서는 백엔드에서만 생성해야 함
 *
 * @param ediDate - 결제 일시
 * @param merchantId - 상점 ID
 * @param amount - 결제 금액
 * @param secretKey - 상점 Secret Key
 * @returns SHA256 해시된 SignData
 */
export const generateSignData = async (
  ediDate: string,
  merchantId: string,
  amount: number | string,
  secretKey: string
): Promise<string> => {
  const validationString = `${ediDate}${merchantId}${amount}${secretKey}`;
  console.log("SignData Generation String:", validationString);

  // Web Crypto API를 사용한 SHA256 해싱
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(validationString);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    // ArrayBuffer를 hex string으로 변환
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    console.log("Generated SignData:", hashHex);
    return hashHex;
  } catch (error) {
    console.error("SignData generation error:", error);
    throw new Error("SignData를 생성할 수 없습니다.");
  }
};

/**
 * SignData 위변조 상세 검증 (백엔드 Secret Key 확인용)
 * 백엔드에서 받은 SignData가 정확한지 다양한 Secret Key로 검증
 *
 * @param ediDate - 결제 일시 (YYYYMMDDHHMISS)
 * @param merchantId - 상점 ID
 * @param amount - 결제 금액
 * @param receivedSignData - 백엔드에서 받은 SignData
 * @returns 검증 결과 {matched: 일치여부, secretKey: 일치한 Secret Key}
 */
export const debugSignDataTamper = async (
  ediDate: string,
  merchantId: string,
  amount: number | string,
  receivedSignData: string
): Promise<{
  received: string;
  matched: boolean;
  matchedSecretKey?: string;
  generatedSignData?: string;
}> => {
  console.log("\n=== 🔍 SignData Tamper Detection (Debug Mode) ===");
  console.log("EdiDate:", ediDate);
  console.log("MerchantId:", merchantId);
  console.log("Amount:", amount);
  console.log("Received SignData from Backend:", receivedSignData);

  // 테스트할 Secret Keys (백엔드에서 사용하는 것으로 추정)
  const secretKeysToTest = [
    // NICEPAY 기본값들
    "EYzu8jGGMfqaDEp76gSckuvnaHHu+bC4opsSN6lHv3b2lurNYkVXrZ7Z1AoqQnXI3eLuaUFyoRNC6FkrzVjceg==",
    "abcd1234", // 테스트용
    "nicepay_secret", // 테스트용
    "test_secret_key", // 테스트용
  ];

  console.log(
    "\n📝 Validation String Pattern: EdiDate + MerchantId + Amount + SecretKey"
  );

  for (const secretKey of secretKeysToTest) {
    try {
      const validationString = `${ediDate}${merchantId}${amount}${secretKey}`;
      console.log(`\n🔑 Testing SecretKey: ${secretKey.substring(0, 20)}...`);
      console.log(`   Validation String: ${validationString}`);

      // SHA256 해싱
      const encoder = new TextEncoder();
      const data = encoder.encode(validationString);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const generatedSignData = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      console.log(`   Generated SignData: ${generatedSignData}`);

      if (generatedSignData === receivedSignData) {
        console.log(`   ✅ MATCHED! Secret Key: ${secretKey}`);
        return {
          received: receivedSignData,
          matched: true,
          matchedSecretKey: secretKey,
          generatedSignData,
        };
      } else {
        console.log(`   ❌ No match`);
      }
    } catch (err) {
      console.error(`   ⚠️ Error testing SecretKey:`, err);
    }
  }

  console.log("\n⚠️ No matching Secret Key found!");
  console.log("Possible issues:");
  console.log("1. Backend Secret Key is different from test keys");
  console.log("2. SignData generation algorithm is different");
  console.log("3. Data was tampered (EdiDate, MerchantId, or Amount changed)");

  return {
    received: receivedSignData,
    matched: false,
  };
};

/**
 * SignData 위변조 검증 리포트 출력
 */
export const printSignDataReport = async (
  ediDate: string,
  merchantId: string,
  amount: number | string,
  receivedSignData: string,
  backendSecretKey?: string
): Promise<void> => {
  console.log("\n╔════════════════════════════════════════════╗");
  console.log("║   NICEPAY SignData Tamper Detection Report   ║");
  console.log("╚════════════════════════════════════════════╝");

  const report = await debugSignDataTamper(
    ediDate,
    merchantId,
    amount,
    receivedSignData
  );

  console.log("\n📊 Report Summary:");
  console.log("─".repeat(50));

  if (report.matched) {
    console.log("✅ Status: DATA INTEGRITY VERIFIED");
    console.log(`   Secret Key Used: ${report.matchedSecretKey}`);
    console.log(`   Generated SignData: ${report.generatedSignData}`);
  } else {
    console.log("⚠️ Status: SECRET KEY MISMATCH");
    console.log(`   Received SignData: ${report.received}`);
    console.log("\n   💡 Next Steps:");
    console.log("   1. Verify backend Secret Key is correct");
    console.log("   2. Check if EdiDate format is correct (YYYYMMDDHHMISS)");
    console.log("   3. Verify MerchantId matches NICEPAY account");
    console.log("   4. Ensure Amount hasn't been modified");

    if (backendSecretKey) {
      console.log(`\n   🔑 Backend Secret Key: ${backendSecretKey}`);
      try {
        const validationString = `${ediDate}${merchantId}${amount}${backendSecretKey}`;
        const encoder = new TextEncoder();
        const data = encoder.encode(validationString);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const shouldBe = hashArray
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        console.log(`   Expected SignData: ${shouldBe}`);
        console.log(
          `   Matches Received: ${
            shouldBe === receivedSignData ? "✅ YES" : "❌ NO"
          }`
        );
      } catch (err) {
        console.error("Error calculating expected SignData:", err);
      }
    }
  }

  console.log("─".repeat(50));
};

/**
 * 결제 데이터 위변조 감지
 * - 백엔드 응답 데이터의 무결성 확인
 *
 * @param paymentData - 결제 데이터
 * @returns 위변조 여부
 */
export const detectPaymentTamper = (
  paymentData: NicepayPaymentRequest
): { isTampered: boolean; issues: string[] } => {
  const issues: string[] = [];

  // 1. 필수 필드 확인
  if (!paymentData.ediDate || !paymentData.signData) {
    issues.push("Missing SignData or EdiDate");
  }

  // 2. EdiDate 형식 확인 (YYYYMMDDHHMISS - 14글자)
  if (paymentData.ediDate && !/^\d{14}$/.test(paymentData.ediDate)) {
    issues.push(`Invalid EdiDate format: ${paymentData.ediDate}`);
  }

  // 3. SignData 형식 확인 (64글자 hex string)
  if (paymentData.signData && !/^[a-f0-9]{64}$/i.test(paymentData.signData)) {
    issues.push(`Invalid SignData format: ${paymentData.signData}`);
  }

  // 4. Amount 유효성 확인 - 정수 값만 허용 (nicepay-pgweb 요구사항)
  const amt =
    typeof paymentData.amt === "string"
      ? parseInt(paymentData.amt, 10)
      : Math.floor(paymentData.amt);

  if (!Number.isInteger(amt)) {
    issues.push(`Amount must be an integer: ${paymentData.amt}`);
  }

  if (amt < 100 || amt > 100000000) {
    // 최소 100원, 최대 1억원
    issues.push(`Suspicious amount: ${amt}`);
  }

  // 5. MerchantId 확인
  if (!paymentData.merchantId || paymentData.merchantId.length < 3) {
    issues.push(`Invalid MerchantId: ${paymentData.merchantId}`);
  }

  console.log("=== Tamper Detection ===");
  console.log("Issues:", issues);

  return {
    isTampered: issues.length > 0,
    issues,
  };
};
